/**
 * /launch — a public landing page bundling the launch assets.
 * The user can:
 *   • Watch the 12-second cinematic launch video
 *   • Download the A3 print poster (PNG, 2480×3508 @ 300dpi)
 *   • Download the social poster (PNG, 1080×1350)
 * Built so the user can share a single URL with the community.
 */
import React, { useRef } from "react";
import { Download, Play, Pause, Share2 } from "lucide-react";

const ASSETS = [
  {
    title: "A3 Print Poster",
    sub: "Noticeboard · 2480 × 3508 · 300 dpi",
    file: "/launch/poster-a3.png",
    preview: "/launch/poster-a3.png",
    note: "Print at A3 (29.7 × 42 cm) for the meeting hall.",
  },
  {
    title: "Social Poster",
    sub: "Instagram & WhatsApp · 1080 × 1350",
    file: "/launch/poster-social.png",
    preview: "/launch/poster-social.png",
    note: "Optimised for portrait social feeds and stories.",
  },
];

export default function Launch() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = React.useState(false);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Ambedkarite Buddhist Global Network UK, now online",
          text: "A new digital home for our sangha. Visit abs-uk.org",
          url: window.location.href,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert("Link copied to clipboard.");
      } catch {
        /* ignore */
      }
    }
  };

  return (
    <>
      {/* HERO with embedded video */}
      <section className="bg-navy text-pearl py-20 lg:py-28" data-testid="launch-hero">
        <div className="section-container max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <p className="eyebrow text-saffron">Launch · 2026</p>
              <h1 className="mt-4 font-display text-5xl sm:text-6xl leading-[1.05]">
                A new digital home <span className="italic text-gold">for our sangha.</span>
              </h1>
              <p className="mt-7 text-pearl/80 leading-relaxed text-[17px]">
                The official website of the <strong>Ambedkarite Buddhist Global Network UK</strong> is
                now online, a cloud-based community uniting members across Britain in the
                spirit of Babasaheb and the path of the Buddha.
              </p>
              <p className="mt-6 font-display italic text-2xl text-gold">
                Education · Equality · Enlightenment
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="/"
                  data-testid="launch-visit-cta"
                  className="px-7 py-3 bg-gold text-navy hover:bg-gold-light rounded-full font-medium"
                >
                  Visit the website
                </a>
                <button
                  onClick={share}
                  data-testid="launch-share-btn"
                  className="inline-flex items-center gap-2 px-5 py-3 border border-pearl/30 text-pearl hover:bg-pearl/10 rounded-full"
                >
                  <Share2 size={16} /> Share with the sangha
                </button>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="relative aspect-video rounded-lg overflow-hidden border border-gold/30 shadow-2xl bg-black">
                <video
                  ref={videoRef}
                  data-testid="launch-video"
                  src="/launch/launch.mp4"
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                  loop
                  preload="metadata"
                  onClick={togglePlay}
                />
                {!playing && (
                  <button
                    onClick={togglePlay}
                    data-testid="launch-video-play"
                    aria-label="Play launch video"
                    className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-colors group"
                  >
                    <span className="w-20 h-20 rounded-full bg-gold/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play size={32} className="text-navy ml-1" fill="currentColor" />
                    </span>
                  </button>
                )}
                {playing && (
                  <button
                    onClick={togglePlay}
                    aria-label="Pause launch video"
                    className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-navy/80 hover:bg-navy text-pearl flex items-center justify-center backdrop-blur-sm"
                  >
                    <Pause size={18} />
                  </button>
                )}
              </div>
              <p className="mt-3 text-xs text-pearl/50 italic text-center">
                12-second cinematic launch reel · made with Sora
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* POSTERS */}
      <section className="py-20 bg-pearl" data-testid="launch-posters">
        <div className="section-container max-w-6xl">
          <div className="max-w-2xl">
            <p className="eyebrow">Posters · ready to share</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
              Print it. Post it. Pass it on.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Download in two formats, the A3 noticeboard version for the meeting hall,
              or the portrait social version for Instagram and WhatsApp. Both are
              high-resolution and free to share within the community.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {ASSETS.map((a) => (
              <div
                key={a.title}
                className="bg-white border border-navy/10 rounded-xl overflow-hidden flex flex-col"
                data-testid={`poster-card-${a.file.split("/").pop().replace(".png", "")}`}
              >
                <div className="aspect-[3/4] bg-navy/5 overflow-hidden flex items-center justify-center p-6">
                  <img
                    src={a.preview}
                    alt={a.title}
                    className="max-w-full max-h-full object-contain shadow-2xl"
                    loading="lazy"
                  />
                </div>
                <div className="p-7 border-t border-navy/10 flex-1 flex flex-col">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-saffron font-semibold">
                    {a.sub}
                  </p>
                  <h3 className="mt-2 font-display text-2xl text-navy">{a.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">
                    {a.note}
                  </p>
                  <a
                    href={a.file}
                    download
                    className="mt-6 inline-flex items-center justify-center gap-2 px-6 py-3 bg-navy text-pearl hover:bg-navy-light rounded-full font-medium"
                  >
                    <Download size={16} /> Download PNG
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INSIDE */}
      <section className="py-20 bg-white" data-testid="launch-features">
        <div className="section-container max-w-5xl text-center">
          <p className="eyebrow">What's inside the website</p>
          <h2 className="mt-3 font-display text-4xl text-navy">A community in eight rooms.</h2>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 text-left">
            {[
              ["Home", "Welcome & vision"],
              ["About", "Mission, history, values"],
              ["The Dhamma", "Teachings + meditation timer"],
              ["Reformers", "Eight figures who lit the path"],
              ["Events", "Gatherings, festivals, RSVPs"],
              ["Children", "Stories & a printable booklet"],
              ["Education", "Programme for UK schools"],
              ["Membership", "Join the sangha"],
            ].map(([title, body]) => (
              <div key={title} className="bg-pearl border border-navy/10 rounded-md p-5">
                <p className="font-display text-xl text-navy">{title}</p>
                <p className="mt-1 text-sm text-slate-600">{body}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 font-display italic text-gold text-xl">
            Jai Bhim · Namo Buddhāya
          </p>
        </div>
      </section>
    </>
  );
}
